package com.crowpay.sdk

import com.crowpay.Credit
import com.crowpay.HasReadByWithFeedbackEndpoints
import com.crowpay.HasReadByEndpoints
import com.crowpay.HasReadByWithCreditsEndpoints
import com.crowpay.MarkCreditsRead
import com.crowpay.ReadByWithFeedbackDataModel
import com.crowpay.ReadByDataModel
import com.crowpay.ReadByWithCreditsDataModel
import com.crowpay.activity
import com.crowpay.extensions.isUnread
import com.lightningkite.UUID
import com.lightningkite.kiteui.AppScope
import com.lightningkite.kiteui.Console
import com.lightningkite.kiteui.models.Icon
import com.lightningkite.kiteui.reactive.Action
import com.lightningkite.kiteui.reactive.ReactiveContext
import com.lightningkite.kiteui.reactive.Readable
import com.lightningkite.kiteui.reactive.ReadableState
import com.lightningkite.kiteui.reactive.invoke
import com.lightningkite.kiteui.reactive.shared
import com.lightningkite.lightningdb.Condition
import com.lightningkite.lightningserver.db.CachingModelRestEndpoints
import com.lightningkite.lightningserver.db.ClientModelRestEndpoints
import com.lightningkite.lightningserver.db.ModelCache
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.serialization.KSerializer
import kotlin.jvm.JvmName
import kotlin.time.Duration
import kotlin.time.Duration.Companion.minutes

open class ReadByModelCache<T : ReadByDataModel>(
    val userId: UUID,
    val cache: ModelCache<T, UUID>,
    open val endpoints: HasReadByEndpoints<T>
) : CachingModelRestEndpoints<T, UUID> by cache {
    @Suppress("UNCHECKED_CAST")
    constructor(
        userId: UUID,
        skipCache: HasReadByEndpoints<T>,
        serializer: KSerializer<T>,
        cacheTime: Duration = 5.minutes,
        showReload: Boolean = false,
        showReloadOnInvalidate: Boolean = false,
        onUpdate: (T) -> Unit = {}
    ) : this(
        userId,
        ModelCache(skipCache as ClientModelRestEndpoints<T, UUID>, serializer, cacheTime, showReload, showReloadOnInvalidate, onUpdate),
        skipCache
    )

    fun localSignalUpdate(matching: (T) -> Boolean, modify: (T) -> T?) = cache.localSignalUpdate(matching, modify)
    fun localInsert(item: T) = cache.localInsert(item)

    private suspend fun update(items: Collection<T>): Map<UUID, T>? {
        val unread = items.filter { it.isUnread(userId) }.map { it._id }
        return if (unread.isEmpty()) null
        else endpoints.markAsRead(unread).associateBy { it._id }
    }

    private suspend fun updateAsUnread(items: Collection<T>): Map<UUID, T>? {
        val read = items.filterNot { it.isUnread(userId) }.map { it._id }
        return if (read.isEmpty()) null
        else endpoints.markAsUnread(read).associateBy { it._id }
    }

    fun localMapUpdate(updates: Map<UUID, T>) = localSignalUpdate(
        matching = { updates.containsKey(it._id) },
        modify = { updates[it._id] }
    )

    suspend fun markAsRead(items: Collection<T>) { update(items)?.let(::localMapUpdate) }
    suspend fun markAsReadDelayUpdate(items: Collection<T>) = update(items)?.let { /*Return local update call*/ { localMapUpdate(it) } }

    suspend fun markAsUnread(items: Collection<T>) { updateAsUnread(items)?.let(::localMapUpdate) }

    protected val modelName = cache.serializer.descriptor.serialName.substringAfterLast('.')

    fun isUnread(debug: Console? = null, items: ReactiveContext.(ReadByModelCache<T>) -> Collection<T>) = UnreadHandler(
        modelName,
        unread = { items(this@ReadByModelCache).isUnread(userId) },
        debug = debug
    )

    fun handle(debug: Console? = null, items: ReactiveContext.(ReadByModelCache<T>) -> Collection<T>) = UnreadHandler(
        modelName,
        unread = { items(this@ReadByModelCache).isUnread(userId) },
        markRead = { markAsRead(items.invoke(this)) },
        markUnread = { markAsUnread(items.invoke(this)) },
        debug = debug
    )

    fun handleDelayUpdate(debug: Console? = null, items: ReactiveContext.(ReadByModelCache<T>) -> Collection<T>) = UnreadHandler(
        modelName,
        unread = { items(this@ReadByModelCache).isUnread(userId) },
        markReadDelayUpdate = { markAsReadDelayUpdate(items.invoke(this)) },
        debug = debug
    )
}

class ReadByWithFeedbackModelCache<T : ReadByWithFeedbackDataModel>(
    userId: UUID,
    cache: ModelCache<T, UUID>,
    override val endpoints: HasReadByWithFeedbackEndpoints<T>,
) : ReadByModelCache<T>(userId, cache, endpoints) {
    @Suppress("UNCHECKED_CAST")
    constructor(
        userId: UUID,
        skipCache: HasReadByWithFeedbackEndpoints<T>,
        serializer: KSerializer<T>,
        cacheTime: Duration = 5.minutes,
        showReload: Boolean = false,
        showReloadOnInvalidate: Boolean = false,
        onUpdate: (T) -> Unit = {}
    ) : this(
        userId,
        ModelCache(skipCache as ClientModelRestEndpoints<T, UUID>, serializer, cacheTime, showReload, showReloadOnInvalidate, onUpdate),
        skipCache
    )

    private suspend fun updateFeedback(items: Collection<T>): Map<UUID, T>? {
        val unread = items.filter { it.feedback?.isUnread(userId) == true }.map { it._id }
        return if (unread.isEmpty()) null
        else endpoints.markFeedbackAsRead(unread).associateBy { it._id }
    }

    private suspend fun updateFeedbackAsUnread(items: Collection<T>): Map<UUID, T>? {
        val read = items.filter { it.feedback?.isUnread(userId) == false }.map { it._id }
        return if (read.isEmpty()) null
        else endpoints.markFeedbackAsUnread(read).associateBy { it._id }
    }

    suspend fun markFeedbackAsRead(items: Collection<T>) { updateFeedback(items)?.let(::localMapUpdate) }
    suspend fun markFeedbackAsReadDelayUpdate(items: Collection<T>) = updateFeedback(items)?.let { println("got update: $it"); { localMapUpdate(it) } }

    suspend fun markFeedbackAsUnread(items: Collection<T>) { updateFeedbackAsUnread(items)?.let(::localMapUpdate) }

    fun feedbackIsUnread(debug: Console? = null, items: ReactiveContext.(ReadByModelCache<T>) -> Collection<T>) = UnreadHandler(
        "$modelName Feedback",
        unread = { items(this@ReadByWithFeedbackModelCache).mapNotNull { it.feedback }.isUnread(userId) },
        debug = debug
    )

    fun handleFeedback(debug: Console? = null, items: ReactiveContext.(ReadByModelCache<T>) -> Collection<T>) = UnreadHandler(
        "$modelName Feedback",
        unread = { items(this@ReadByWithFeedbackModelCache).mapNotNull { it.feedback }.isUnread(userId) },
        markRead = { markFeedbackAsRead(items.invoke(this)) },
        markUnread = { markFeedbackAsUnread(items.invoke(this)) },
        debug = debug
    )
}

class ReadByWithCreditsModelCache<T : ReadByWithCreditsDataModel>(
    userId: UUID,
    cache: ModelCache<T, UUID>,
    override val endpoints: HasReadByWithCreditsEndpoints<T>
) : ReadByModelCache<T>(userId, cache, endpoints) {
    @Suppress("UNCHECKED_CAST")
    constructor(
        userId: UUID,
        skipCache: HasReadByWithCreditsEndpoints<T>,
        serializer: KSerializer<T>,
        cacheTime: Duration = 5.minutes,
        showReload: Boolean = false,
        showReloadOnInvalidate: Boolean = false,
        onUpdate: (T) -> Unit = {}
    ) : this(
        userId,
        ModelCache(skipCache as ClientModelRestEndpoints<T, UUID>, serializer, cacheTime, showReload, showReloadOnInvalidate, onUpdate),
        skipCache
    )

    private fun List<Credit>.filter(condition: Condition<Credit>?) = if (condition != null) filter { condition(it) } else this

    private suspend fun updateCredits(items: Collection<T>, condition: Condition<Credit>?): Map<UUID, T>? {
        val unread = items.filter { it.credits.filter(condition).isUnread(userId) }.map { it._id }
        return if (unread.isEmpty()) null
        else endpoints.markCreditsAsRead(MarkCreditsRead(unread, condition)).associateBy { it._id }
    }

    private suspend fun updateCreditsAsUnread(items: Collection<T>, condition: Condition<Credit>?): Map<UUID, T>? {
        val unread = items.filterNot { it.credits.filter(condition).isUnread(userId) }.map { it._id }
        return if (unread.isEmpty()) null
        else endpoints.markCreditsAsUnread(MarkCreditsRead(unread, condition)).associateBy { it._id }
    }

    suspend fun markCreditsAsRead(items: Collection<T>, condition: Condition<Credit>? = null) { updateCredits(items, condition)?.let(::localMapUpdate) }
    suspend fun markCreditsAsUnread(items: Collection<T>, condition: Condition<Credit>? = null) { updateCreditsAsUnread(items, condition)?.let(::localMapUpdate) }

    suspend fun markCreditsAsReadDelayUpdate(items: Collection<T>, condition: Condition<Credit>? = null) = updateCredits(items, condition)?.let { { localMapUpdate(it) } }

    private val debugName = "$modelName Credits"

    fun handleCredits(condition: Condition<Credit>? = null, debug: Console? = null, items: ReactiveContext.() -> Collection<T>) = UnreadHandler(
        debugName,
        unread = { items().flatMap { it.credits.filter(condition) }.isUnread(userId) },
        markRead = { markCreditsAsRead(items(), condition) },
        markUnread = { markCreditsAsUnread(items(), condition) },
        debug = debug
    )

    fun handleCreditsDelayUpdate(delay: Duration, condition: Condition<Credit>? = null, debug: Console? = null, items: ReactiveContext.() -> Collection<T>) = UnreadHandler(
        debugName,
        unread = { items().flatMap { it.credits.filter(condition) }.isUnread(userId) },
        markRead = {
            AppScope.launch {
                delay(delay)
                markCreditsAsRead(items(), condition)
            }
        },
        debug = debug
    )

    fun creditsIsUnread(debug: Console? = null, condition: Condition<Credit>? = null, items: ReactiveContext.() -> Collection<T>) = UnreadHandler(
        debugName,
        unread = { items().flatMap { it.credits.filter(condition) }.isUnread(userId) },
        debug = debug
    )
}


class UnreadHandler(
    val name: String,
    private val unread: ReactiveContext.(userId: UUID) -> Boolean,
    val markRead: (suspend () -> Unit)? = null,
    val markReadDelayUpdate: (suspend () -> (() -> Unit)?)? = null,
    val markUnread: (suspend () -> Unit)? = null,
    val debug: Console? = null
): Readable<Boolean> {
    val markAllAsReadAction = markRead?.let { Action("Mark As Read", Icon.activity, action = it) }
    val markAllAsUnreadAction = markUnread?.let { Action("Mark As Unread", Icon.close, action = it) }

    companion object {
        val Never = UnreadHandler("Never", unread = { false })
    }

    fun ReactiveContext.isUnread(userId: UUID) = unread(userId).also { if (it) debug?.info("$name is unread") }

    private val shared by lazy { shared { isUnread(notNullSession().userId) } }

    override val state: ReadableState<Boolean> by shared::state
    override fun addListener(listener: () -> Unit): () -> Unit = shared.addListener(listener)

    private operator fun (suspend () -> Unit)?.plus(other: (suspend () -> Unit)?) =
        if (this != null && other != null) { { this() ; other() } }
        else this ?: other

    @JvmName("delayUpdatePlus")
    private operator fun (suspend () -> (() -> Unit)?)?.plus(other: (suspend () -> (() -> Unit)?)?): (suspend () -> (() -> Unit)?)? =
        if (this != null && other != null) {
            {
                val update1 = this()
                val update2 = other();
                { update1?.invoke(); update2?.invoke() }
            }
        }
        else this ?: other

    operator fun plus(other: UnreadHandler) = UnreadHandler(
        "$name+${other.name}",
        unread = { isUnread(it) || other.run { isUnread(it) } },
        markRead = this.markRead + other.markRead,
        markReadDelayUpdate = this.markReadDelayUpdate + other.markReadDelayUpdate,
        markUnread = this.markUnread + other.markUnread,
        debug = null
    )
}